import React from "react";
import ParamTableEditorDialog from "./param-table-editor-dialog";
import GraphIntervalSelectorDialog from "./graph-interval-selector-dialog";
import MedicalReportEditorDialog from "./medical-report-editor-dialog";
import AimDialog from "./aim-dialog";
import WaitDialog from "./wait-dialog";

import withLocalization from "../../hoc/with-localization";
import WholeGraphDialog from "./choose-cmpx-dialog";
import {
    paramTable_dialog,
    graphIntervals_dialog,
    medicalReport_dialog,
    chooseCmpx_dialog,
    wait_dialog,
    measurer_dialog,
    aim_dialog
} from "./constants";
import { connect } from "react-redux";
import {
    closeModalDialog,
    reportDataChanged,
    prepareDataForSave,
    changeRepresentativeCmpx,
    verificateCmpx,
    fetchPreviewMedConcl,
    fetchPreviewDerivedParams,
    changeReportSettings
} from "../../redux/actions/report-actions.js";
import { modal_mode_halfscreen, _param_edited } from "../../redux/actions/actionConstants";
import { bindActionCreators } from "redux";
import compose from "../../utils/compose";
import withReportDataService from "../../hoc/with-dataservice-consumer.jsx";
import getDiffProperties from "../../utils/getDiffProperties";

import "./popup-dialog-manager.scss";
import "./edit.scss";
import MeasurerGraphDialog from "./measurer-graph-dialog";

class PopupDialogManager extends React.Component {
    async onSave(dialog, outputDataFromDialog) {
        const {
            reportData: { report, settings },
            updateReportData,
            updateReportSettings,
            prepareDataForSave,
            changeCmpx,
            verificateCmpx,
            reqUpdated_MedicalConclusion
        } = this.props;
        let preparedDiff = this.prepareDifference(dialog, outputDataFromDialog);
        const nothingChanged = Object.keys(preparedDiff).length === 0;
        if (nothingChanged) return;

        prepareDataForSave({ diff: preparedDiff });

        switch (dialog) {
            case paramTable_dialog: {
                this.updateReportData_ParamTableDialog(report, settings, outputDataFromDialog);
                break;
            }
            case graphIntervals_dialog: {
                if ("verificated" in preparedDiff) verificateCmpx(true);
                await this.updateReportData_GraphIntervalsDialog(report, settings, outputDataFromDialog);
                break;
            }
            case measurer_dialog:
            case medicalReport_dialog: {
                this.updateReportData_MedicalConclusion(report, outputDataFromDialog);
                break;
            }
            case aim_dialog: {
                this.updateReportData_AimAppointed(report, outputDataFromDialog);
                break;
            }
            case chooseCmpx_dialog: {
                if (outputDataFromDialog.Q !== undefined) await changeCmpx(outputDataFromDialog);

                return;
            }
            default:
                return;
        }

        await reqUpdated_MedicalConclusion();

        updateReportSettings(settings);
        updateReportData(report);
    }

    prepareDifference(dialog, outputData) {
        let preparedDiff = {};
        let { diff } = getDiffProperties(this.extractEditData(dialog), outputData);
        diff.filter(({ key }) => key in outputData).forEach(({ key, val }) => {
            preparedDiff[key] = val;
        });

        return preparedDiff;
    }

    ms_pnt = val => (val === null || val === undefined || isNaN(val) ? null : Math.round((val * this.props.fd) / 1000));

    async updateReport_WithRecalcedDerivedParams(report, settings, outputDataFromDialog) {
        // Запрос перерассчитанных параметров у сервера (напр. QTc)
        const { ms_pnt } = this;
        const repCmpx = report.representativeCmpx;
        const dataDerivParams = {
            Q: ms_pnt(outputDataFromDialog?.Q) ?? repCmpx.Q.val,
            S: ms_pnt(outputDataFromDialog?.S) ?? repCmpx.S.val,
            P: ms_pnt(outputDataFromDialog?.P) ?? repCmpx.P.val,
            PDur: ms_pnt(outputDataFromDialog?.PDur) ?? repCmpx.PDur.val,
            QT: ms_pnt(outputDataFromDialog?.QTmax) ?? repCmpx.QTmax.val,
            QTcAlg: outputDataFromDialog?.QTcAlg ?? settings.QTcAlg.val,
            ReprCmxIndex: repCmpx.index
        };
        const {
            signalLength,
            fd,
            graph12Leads: { start_pnt, timeScale_mm_s }
        } = this.props;
        const from = start_pnt;
        const width_12LeadsGraph_pts = (180 / timeScale_mm_s) * fd; // 180 mm
        const to = Math.min(signalLength, from + width_12LeadsGraph_pts);

        const recalcedParams = await this.props.reqPreviewDerivedParams(from, to, dataDerivParams);
        this.updateRecalcedParams(report, settings, recalcedParams);
    }

    updateReportData_ParamTableDialog(report, settings, outputDataFromDialog) {
        const {
            HR,
            RRmin,
            RRmax,
            S,
            P,
            PDur,
            QTmax,
            QTc,
            angle_QRS,
            angle_P,
            angle_T,
            STDeprTo_uV,
            STElevTo_uV,
            transZone,
            indexSokolovLyon,
            QTcAlg
        } = outputDataFromDialog;
        const updatedParams = {
            HR: { val: HR, state: _param_edited },
            RRmin: { val: RRmin, state: _param_edited },
            RRmax: { val: RRmax, state: _param_edited }
        };
        const updatedReprCmpx = {
            S: { val: S, state: _param_edited },
            P: { val: P, state: _param_edited },
            PDur: { val: PDur, state: _param_edited },

            QTmax: { val: QTmax, state: _param_edited },
            QTc: { val: QTc, state: _param_edited },

            angles: {
                QRS: { val: angle_QRS, state: _param_edited },
                P: { val: angle_P, state: _param_edited },
                T: { val: angle_T, state: _param_edited }
            },

            STDeprTo_uV: { val: STDeprTo_uV, state: _param_edited },
            STElevTo_uV: { val: STElevTo_uV, state: _param_edited },

            transZone: { val: transZone, state: _param_edited },
            indexSokolovLyon: { val: indexSokolovLyon, state: _param_edited }
        };
        report.HR_RR = {
            ...report.HR_RR,
            ...updatedParams
        };
        report.representativeCmpx = {
            ...report.representativeCmpx,
            ...updatedReprCmpx
        };
        settings.QTcAlg = { val: QTcAlg, state: _param_edited };
    }

    async updateReportData_GraphIntervalsDialog(report, settings, outputDataFromDialog) {
        const { Q, S, PDur, P, QTmax, QTc } = outputDataFromDialog;
        report.representativeCmpx = {
            ...report.representativeCmpx,
            Q: { val: Q, state: _param_edited },
            S: { val: S, state: _param_edited },
            P: { val: P, state: _param_edited },
            PDur: { val: PDur, state: _param_edited },
            QTmax: { val: QTmax, state: _param_edited },
            QTc: { val: QTc, state: _param_edited },
            verificated: outputDataFromDialog.verificated
        };
        await this.updateReport_WithRecalcedDerivedParams(report, settings, outputDataFromDialog);
    }

    updateReportData_MedicalConclusion(report, outputDataFromDialog) {
        report.medicalConclusion = {
            ...report.medicalConclusion,
            val: outputDataFromDialog.medicalConclusion,
            state: _param_edited
        };
    }

    updateReportData_AimAppointed(report, outputDataFromDialog) {
        report.aim = { val: outputDataFromDialog.aim, state: _param_edited };
        report.appointed = { val: outputDataFromDialog.appointed, state: _param_edited };
    }

    updateRecalcedParams(report, settings, recalcedParams) {
        report.representativeCmpx = {
            ...report.representativeCmpx,
            ...recalcedParams.representativeCmpx
        };
        report.HR_RR = {
            ...report.HR_RR,
            ...recalcedParams.HR_RR
        };
        settings = {
            ...settings,
            QTcAvailableAlgs: recalcedParams.QTcAvailableAlgs,
            QTcAlg: { val: recalcedParams.QTcAlg }
        };
    }

    extractEditData(dialog) {
        const {
            reportData: { report, settings },
            useExternalConclusionConstructor
        } = this.props;
        switch (dialog) {
            case paramTable_dialog: {
                const {
                    representativeCmpx: {
                        S,
                        P,
                        PDur,
                        QTmax,
                        RR,
                        Q,
                        QTc,
                        Q_next,
                        angles: {
                            QRS: { val: angle_QRS },
                            P: { val: angle_P },
                            T: { val: angle_T }
                        },
                        STDeprTo_uV,
                        STElevTo_uV,
                        transZone,
                        indexSokolovLyon,
                        QTcAlgVals
                    },
                    HR_RR: { HR, RRmin, RRmax }
                } = report;
                const { QTcAlg, QTcAvailableAlgs } = settings;
                const data = {
                    HR: HR.val,
                    RRmin: RRmin.val,
                    RRmax: RRmax.val,
                    RR: RR.val,
                    Q_next: Q_next !== undefined && Q_next !== null ? Q_next.val - Q.val : undefined,
                    S: S.val,
                    P: P.val,
                    PDur: PDur.val,

                    QTmax: QTmax.val,

                    angle_QRS,
                    angle_P,
                    angle_T,

                    STDeprTo_uV: STDeprTo_uV.val,
                    STElevTo_uV: STElevTo_uV.val,
                    transZone: transZone.val,
                    indexSokolovLyon: indexSokolovLyon.val,

                    QTc: QTc.val,
                    QTcAlg: QTcAlg.val,
                    QTcAvailableAlgs,
                    QTcAlgVals
                };
                return data;
            }
            case aim_dialog: {
                const { aim, appointed } = report;
                const data = {
                    aim: aim?.val,
                    appointed: appointed?.val
                };
                return data;
            }
            case graphIntervals_dialog: {
                const { Q, QTc, S, P, PDur, QTmax, RR, QTcAlgVals } = report.representativeCmpx;
                const { QTcAlg, QTcAvailableAlgs } = settings;

                const data = {
                    Q: Q.val,
                    QTc: QTc.val,
                    S: S.val,
                    P: P.val,
                    PDur: PDur.val,
                    QTmax: QTmax.val,
                    RR_ms: RR.val,
                    verificated: this.props.cmpxVerificated,
                    QTcAlg: QTcAlg.val,
                    QTcAvailableAlgs,
                    QTcAlgVals
                };

                return data;
            }
            case medicalReport_dialog: {
                const { representativeCmpx, HR_RR, exam_date, patient, stimulatorType } = report;
                const { val: medicalConclusion, auto: autoConclusion } = report.medicalConclusion;
                return {
                    medicalConclusion,
                    autoConclusion,
                    representativeCmpx,
                    patient,
                    exam_date,
                    RRParams: HR_RR,
                    useExternalConclusionConstructor,
                    stimulatorType
                };
            }
            case chooseCmpx_dialog: {
                const { Q, S } = report.representativeCmpx;
                const data = {
                    Q: Q.val,
                    S: S.val
                };
                return data;
            }
            case measurer_dialog: {
                const { Q, S } = report.representativeCmpx;
                const data = {
                    Q: Q.val,
                    S: S.val
                };
                const { val: medicalConclusion } = report.medicalConclusion;
                return { medicalConclusion, ...data };
            }
            default:
                return null;
        }
    }

    render() {
        const { requesting, opened, dialog, dialogMode, loc, closeModalDialog, reportData, fd, needCmpxVerification, cmpxVerificated } =
            this.props;

        let dialogComponent = null;
        if (reportData) {
            const data = this.extractEditData(dialog);

            // Dialog props and event handlers
            const dlgProps = {
                loc,
                data,
                onClose: () => closeModalDialog(),
                onSave: data => this.onSave(dialog, data)
            };

            // Create component
            dialogComponent = (dlg => {
                switch (dlg) {
                    case paramTable_dialog:
                        dlgProps.fd = fd;
                        return <ParamTableEditorDialog {...dlgProps} />;
                    case aim_dialog:
                        return <AimDialog {...dlgProps} />;
                    case graphIntervals_dialog:
                        dlgProps.fd = fd;
                        return <GraphIntervalSelectorDialog {...dlgProps} {...{ needCmpxVerification, cmpxVerificated }} />;
                    case medicalReport_dialog:
                        dlgProps.fd = fd;
                        return <MedicalReportEditorDialog {...dlgProps} conclusionService={this.props.conclusionService} />;
                    case chooseCmpx_dialog:
                        dlgProps.fd = fd;
                        return <WholeGraphDialog {...dlgProps} />;
                    case wait_dialog:
                        return <WaitDialog loc={loc} />;

                    case measurer_dialog:
                        return <MeasurerGraphDialog {...dlgProps} />;
                    default:
                        return null;
                }
            })(dialog);

            if (requesting) dialogComponent = <WaitDialog loc={loc} />;
        }

        const clazz =
            "popup-overlay" +
            (opened || requesting ? " popup-overlay_opened" : "") +
            (dialogMode === modal_mode_halfscreen ? " popup-overlay_halfscreen" : "");
        return <div className={clazz}>{dialogComponent}</div>;
    }
}

const mapStateToProps = ({
    reportApp: {
        app: {
            requestingState: { requesting },
            modal: { opened, dialog, dialogMode }
        },
        reportData
    }
}) => {
    const { signal: { fd, signalLength } = {} } = reportData?.report ?? {};
    const { graph12Leads } = reportData?.settings?.graphs ?? {};
    const { NeedCmpxIntervalsVerification: needCmpxVerification, useExternalConclusionConstructor } = reportData?.settings ?? {};
    const { verificated } = reportData?.report?.representativeCmpx ?? {};
    return {
        opened,
        dialog,
        dialogMode,
        reportData,
        needCmpxVerification,
        useExternalConclusionConstructor,
        cmpxVerificated: verificated,
        requesting,
        fd,
        signalLength,
        graph12Leads
    };
};

const mapDispatchToProps = (
    dispath,
    {
        dataService: {
            changeReprCmpx: changeReprCmpxAPI,
            getReportData,
            check,
            previewMedicalReport,
            onResIDChangedCallback,
            previewDerivedParams
        }
    }
) => {
    return bindActionCreators(
        {
            closeModalDialog,
            updateReportData: reportDataChanged,
            updateReportSettings: changeReportSettings,
            prepareDataForSave,
            verificateCmpx,
            changeCmpx: changeRepresentativeCmpx(changeReprCmpxAPI, getReportData, check, onResIDChangedCallback),
            reqUpdated_MedicalConclusion: fetchPreviewMedConcl(previewMedicalReport),
            reqPreviewDerivedParams: fetchPreviewDerivedParams(previewDerivedParams)
        },
        dispath
    );
};

export default compose(withLocalization, withReportDataService(), connect(mapStateToProps, mapDispatchToProps))(PopupDialogManager);
